// src/pages/Loans.tsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Hero from "../components/Hero";
import Container from "../components/ui/Container";
import { PrimaryButton } from "./ui/Buttons";
import SectionHeader from "./ui/SectionHeader";

// Loan type definition
interface Loan {
    id: string;
    type: string;
    desc: string;
    details: string[];
    documents: string[];
}

// LoanCard Component
function LoanCard({ loan }: { loan: Loan }) {
    const [open, setOpen] = useState(false);

    return (
        <div className="p-6 bg-white shadow rounded-lg hover:shadow-lg transition">
            <h4 className="text-xl font-bold text-blue-600">{loan.type}</h4>
            <p className="mt-2 text-gray-600">{loan.desc}</p>

            <button
                onClick={() => setOpen(!open)}
                className="mt-3 text-sm text-blue-500 underline"
            >
                {open ? "Hide Details" : "Read More"}
            </button>

            {open && (
                <>
                    <ul className="mt-3 list-disc list-inside text-gray-700 space-y-2">
                        {loan.details.map((point, i) => (
                            <li key={i}>{point}</li>
                        ))}
                    </ul>

                    <h5 className="mt-4 font-semibold text-gray-800">Required Documents:</h5>
                    <ul className="mt-2 list-disc list-inside text-gray-700 space-y-1">
                        {loan.documents.map((doc, i) => (
                            <li key={i}>{doc}</li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
}

export default function Loans() {
    const navigate = useNavigate();

    const loans: Loan[] = [
        {
            id: "personal",
            type: "Personal Loan",
            desc: "Flexible repayment options for personal needs like travel, weddings, or emergencies.",
            details: [
                "Loan amount: ₹50,000 – ₹20,00,000",
                "Tenure: 1 – 5 years",
                "Interest rate: Starting from 10.5% p.a.",
                "No collateral required",
            ],
            documents: [
                "Identity Proof (Aadhar/PAN/Passport)",
                "Address Proof",
                "Income Proof (Salary slip or IT return)",
                "Bank Statements last 6 months",
            ],
        },
        {
            id: "home",
            type: "Home Loan",
            desc: "Affordable EMIs to buy your dream house with minimal documentation.",
            details: [
                "Loan amount: Up to 80% of property value",
                "Tenure: Up to 30 years",
                "Tax benefits under section 80C & 24(b)",
                "Balance transfer facility available",
            ],
            documents: [
                "Identity Proof",
                "Address Proof",
                "Income Proof (Salary slip / IT return / 3 Years ITR Return)",
                "Property Papers",
                "Bank Statements last 6 months",
            ],
        },
        {
            id: "business",
            type: "Business Loan",
            desc: "Support to grow your business with working capital, equipment financing, and expansion loans.",
            details: [
                "Loan amount: ₹1,00,000 – ₹5 Cr",
                "Custom repayment schedule",
                "Collateral-free for small ticket loans",
                "Quick processing for SMEs",
            ],
            documents: [
                "Business Registration Proof",
                "Financial Statements (last 2 years)",
                "Bank Statements last 6 months",
                "Identity & Address Proof of owners",
            ],
        },
        {
            id: "car",
            type: "Car Loan",
            desc: "Get your dream car with low-interest rates and easy repayment options.",
            details: [
                "Loan amount: Up to 90% of car value",
                "Tenure: 1 – 7 years",
                "Attractive interest rates",
                "New & used cars eligible",
            ],
            documents: [
                "Identity & Address Proof",
                "Income Proof",
                "Car Quotation / Invoice",
                "Bank Statements last 6 months",
            ],
        },
        {
            id: "education",
            type: "Educational Loan",
            desc: "Finance your or your child’s higher education with flexible terms.",
            details: [
                "Loan amount: ₹50,000 – ₹50,00,000",
                "Covers tuition, hostel, travel & books",
                "Moratorium period available",
                "Subsidy schemes for eligible students",
            ],
            documents: [
                "Identity & Address Proof",
                "Admission Letter",
                "Fee Structure",
                "Income Proof of Parent/Guardian",
                "Bank Statements last 6 months",
            ],
        },
        {
            id: "lap",
            type: "Loan Against Property (LAP)",
            desc: "Leverage your property to get funds for personal or business needs.",
            details: [
                "Loan amount: Up to 60% of property value",
                "Tenure: Up to 15 years",
                "Lower interest compared to personal loans",
                "Residential & commercial properties accepted",
            ],
            documents: [
                "Property Papers",
                "Identity & Address Proof",
                "Income Proof",
                "Bank Statements last 6 months",
                "Property Valuation Report",
            ],
        },
    ];

    const partners = [
        { name: "Thawa FSPL", desc: "Financial Services Partner", path: "/thawa" },
    ];

    interface ChecklistCardProps {
            title: string;
            items: string[];
        }
    
        const checklists = [
            {
                title: "Housing Loan",
                items: [
                    "Photo - 2 nos",
                    "Aadhar copies - 2 nos",
                    "Pan card copies - 2 nos",
                    "ID card copy",
                    "Appointment order",
                    "Last 3 months payslip (app)",
                    "Last 6 months Bank statement (app)",
                    "Last 2 years Form 16 (app)",
                    "Document copy",
                    "Building plan and proceeding letter",
                    "Actual plan",
                    "Estimate",
                    "1987 to till date EC",
                  ],
            },
            {
                title: "Business People Loan",
                items: [
                    "Photo - 2 nos",
                    "Aadhar copies - 2 nos",
                    "Pan card copies - 2 nos",
                    "ID card copy",
                    "Appointment order",
                    "Last 3 months payslip (app)",
                    "Last 6 months Bank statement (app)",
                    "Last 2 years Form 16 (app)",
                    "Document copy",
                    "Building plan and proceeding letter",
                    "Actual plan",
                    "Estimate",
                    "1987 to till date EC",
                  ],
            },
            {
                title: "NRI Loan",
                items: [
                    "Professional - Max term 20 years (1 year NRI experience required)",
                    "Non-professional - Max term 15 years (3 years NRI experience required)",
                    "Two photos with application Griha Shoba form",
                    "NRI Undertaking Letter",
                    "CKY forms from applicant and POA",
                    "Educational qualification proof",
                    "Passport & latest Visa copy",
                    "Latest 6 months payslip",
                    "Bank statement for 6 months of salary credit bank – ORIGINAL",
                    "Previous employment proof - NRI experience proof REQUIRED",
                    "NRE/NRO latest bank account statement for 6 months",
                    "Email confirmation from employer (HR mail ID required)",
                    "EMI repayment through NACH & 6 months PDCs required",
                    "All documents to be attested by Indian Embassy or Notary Public or LIC Housing Finance – Overseas Branch",
                    "Latest month payslip / Salary certificate & latest month Bank statement",
                    "Appointment order OR Contractor passport & latest Visa copy",
                    "Power of attorney in LIC HFL format (attested by Indian embassy or Notary Public)",
                    "NOTE: All documents must be self-attested",
                  ],
            },
        ];
    
        function ChecklistCard({ title, items }: ChecklistCardProps) {
            const [open, setOpen] = useState(false);
    
            return (
                <div className="p-6 bg-white shadow rounded-lg hover:shadow-lg transition">
                    <h3 className="text-xl font-bold">{title}</h3>
                    <button
                        onClick={() => setOpen(!open)}
                        className="mt-3 text-sm text-blue-500 underline"
                    >
                        {open ? "Hide Checklist" : "Show Checklist"}
                    </button>
    
                    {open && (
                        <ul className="mt-3 list-disc list-inside text-gray-700 space-y-2">
                            {items.map((item, idx) => (
                                <li key={idx}>{item}</li>
                            ))}
                        </ul>
                    )}
                </div>
            );
        }

    return (
        <>
            <Hero />

            {/* Loans Section */}
            <section id="loans" className="py-16 bg-gray-50">
                <Container>
                    {/* Main Section Heading */}
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">Loans</h2>
                    <p className="text-gray-600 max-w-3xl mb-8">
                        We provide a variety of loan options to meet your personal, business, and property needs. Explore our flexible and affordable solutions to achieve your goals.
                    </p>

                    {/* Our Loan Products Heading */}
                    <h2 className="text-2xl font-bold mb-6">Our Loan Products</h2>

                    {/* Loan Cards */}
                    <div className="grid md:grid-cols-3 gap-6 mb-12">
                        {loans.map((loan) => (
                            <LoanCard key={loan.id} loan={loan} />
                        ))}
                    </div>

                    {/* Our Partners Heading */}
                    <h2 className="text-2xl font-bold mb-6">Our Partners</h2>

                    {/* Partners Section */}
                    <div className="grid md:grid-cols-3 gap-6">
                        {partners.map((p, i) => (
                            <div
                                key={i}
                                onClick={() => navigate(p.path)}
                                className="p-4 bg-white shadow rounded-lg hover:shadow-lg transition cursor-pointer"
                            >
                                <h4 className="font-semibold text-blue-600">{p.name}</h4>
                                <p className="text-gray-600">{p.desc}</p>
                            </div>
                        ))}
                    </div>
                </Container>
            </section>

            {/* CTA Section */}
            <section className="py-16 bg-blue-50">
                <Container className="text-center">
                    <h2 className="text-2xl font-bold mb-4">Start Your Loan Application</h2>
                    <p className="text-gray-700 mb-6">
                        Contact us today to explore the best loan options for your needs.
                    </p>
                    <PrimaryButton onClick={() => (window.location.href = "/contact")}>
                        Contact Us
                    </PrimaryButton>
                </Container>
            </section>

            <section className="py-16 bg-gray-50">
                <Container>
                    <SectionHeader
                        title="Loan Checklists"
                        subtitle="Documents and requirements for each loan type"
                    />

                    <div className="grid md:grid-cols-3 gap-6 mt-6">
                        {checklists.map((checklist, idx) => (
                            <ChecklistCard key={idx} title={checklist.title} items={checklist.items} />
                        ))}
                    </div>
                </Container>
            </section>
        </>
    );
}
